/** Game constants for Rogue's Cog */

export const CONFIG = {
  BOARD_WIDTH_PX: 375,
  BOARD_HEIGHT_PX: 667,
  CELL_SIZE: 18,
  SHOOTER_Y: 620,
  PROJECTILE_SPEED: 12,
  WALL_LEFT_X: -9,
  WALL_RIGHT_X: 18 * (9 * 2 + 1) + 9,  // rightmost odd-row bubble + half cell = 351
  MIN_CLUSTER_SIZE: 3,
  MAX_COLORS: 6,
  INITIAL_ROWS: 7,
  INITIAL_ACTIVE_COLORS: 5,
  FREE_PLAYS_DAILY: 2,
  COLOR_UNLOCK_TURNS: [8, 15, 30],
  DRAFT_GRACE_PERIOD: 3,
  FIRST_DRAFT_TURN: 7,
  /** Drafts per interval step: the gap between drafts grows by one turn every N drafts (7,7,7,8,8,8,...). */
  DRAFT_INTERVAL_STEP: 3,
  /** Ceiling descent: initial interval between row injections (ms). */
  DESCENT_INITIAL_INTERVAL_MS: 15_000,
  /**
   * Ceiling descent: minimum interval at max difficulty (ms).
   * 4_000 / 1.15 — the final scroll speed is 15% faster than the original
   * 4s cadence, compensating for Mk. V blueprints staying in the draft pool.
   */
  DESCENT_MIN_INTERVAL_MS: 3_478,
  /** Time (ms) at which descent reaches max rate. 420s * 1.15 — the ramp runs 15% longer. */
  DESCENT_RAMP_DURATION_MS: 483_000,
  /**
   * Dynamic scroll speedup: when the board has fewer colored-gear rows
   * visible, the ceiling descends faster to keep skilled players engaged.
   * At or above THRESHOLD row-equivalents of visible gears, descent runs at
   * baseline (1x). At 0 rows, it runs at MAX_MULTIPLIER. Linearly interpolated
   * between. A "row-equivalent" is (visible non-obstacle bubbles / BOARD_COLUMNS).
   */
  DESCENT_SPEEDUP_THRESHOLD_ROWS: 5,
  DESCENT_SPEEDUP_MAX_MULTIPLIER: 6,
  AIM_ANGLE_MIN: 0.2618,
  AIM_ANGLE_MAX: 2.8798,
  AIM_ANGLE_PRECISION: 4,
  AIM_OFFSET_Y: 70,
  NEAR_FAILURE_ROWS: 3,
  /**
   * Multiplier: +0.5x (5) per clearing shot, -0.5x (5) per miss — a miss
   * costs exactly one hit. (The old -1.0x standard decay punished a single
   * miss harder than two hits could repay.) Onboarding and standard decay
   * currently coincide; the tier split is kept so it can be re-tuned.
   */
  MULTIPLIER_DECAY_ONBOARDING: 5,
  MULTIPLIER_DECAY_STANDARD: 5,
  /** Daily challenge: shot budget. Running out = victory (tower conquered). */
  DAILY_SHOT_BUDGET: 100,
  /** Daily challenge: initial rows of gears — 50% more than INITIAL_ROWS. */
  DAILY_INITIAL_ROWS: 11,
  /** Daily challenge: multiplier applied to ceiling descent speed (1.5x). */
  DAILY_DESCENT_MULTIPLIER: 1.5,
  MAX_MULTIPLIER_X10: 50,
  MIN_MULTIPLIER_X10: 10,
  BOMB_RADIUS: 1,
  KEYBOARD_AIM_SPEED: 2.5,
  /** Highest valid q index. Both even and odd rows use q=0..BOARD_COLUMNS
   * (10 gears per row). The odd-row half-cell offset means odd rows extend
   * slightly further right visually, but no row is "missing" a gear. */
  BOARD_COLUMNS: 9,
  /**
   * Brief hold (ms) after the projectile lands but before any pop/drop removal.
   * Lets the player see the cluster they just formed before it evaporates. Only
   * applied when the shot actually removes bubbles; otherwise the animation ends
   * immediately on landing.
   */
  LAND_HOLD_MS: 100,
} as const;
